import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import {
  FullHeightGridRoot,
  HideNullTitlesHeaderContent,
  HoverableTable,
  ScrollTableOnScreenToTop,
  StickyHeaderRow,
  useColumnResizingState,
  useConvertedSorting,
} from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { HL7MessageTypeNamesDict } from '@lmt/unicorn-shared/data/messageTypeNamesDict';
import React, { FC, useEffect, useMemo, useCallback } from 'react';
import { Paper } from '@mui/material';
import {
  HL7MessageDto,
  HL7MessageDirection,
  FileInfoDto,
} from '@lmt/shared-types';
import { snakeCase } from 'lodash';
import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import {
  DXRG,
  SortingState,
  Table,
  TableColumnWidthInfo,
  TableRowDetail,
  useReactDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid';
import { HL7DocumentItem } from './DocumentItem';
import { getFullName } from '../../../patients/patient-form/common/common';
import { useReactDataGridLocalization } from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid-localization';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';

function dateToFormat(date: Date): string {
  return localFormat(date, 'Ppp');
}

function getFormattedDateTime(date?: Date | null): string {
  return date ? dateToFormat(date) : '';
}

type Props = {
  noResults: boolean;
  data: HL7MessageDto[];
  documents: FileInfoDto[];
  sorting: { sortBy?: string | null; desc?: boolean };
  changeSorting: (sorting: { sortBy?: string | null; desc?: boolean }) => void;
  reparseMessage: (messageId: number) => void;
  reparseMessageWithFilter: (messageId: number) => void;
  onDelete: (messageId: number) => void;
  onDownloadMessage: (messageId: number) => void;
  onShowFullMessage: (messageId: number) => void;
  searchText?: string | null;
};

const enum TableKeys {
  id = 'id',
  dateTime = 'dateTime',
  type = 'type',
  messageDirection = 'messageDirection',
  isAccepted = 'isAccepted',
  patient = 'patient',
  document = 'document',
  error = 'error',
  actions = 'actions',
}

export const MessagesTable: FC<Props> = React.memo(
  function MessagesTable(props) {
    useReactDataGrid();
    const localizationMessages = useReactDataGridLocalization();
    const { searchText } = props;

    const i18n = useScopedTranslation('hl7.messages');
    const i18nGlobal = useTranslation();
    const tabs = useTabs();

    const openPatientProfile = useCallback((patientId: number) => {
      tabs.openTab(pages.patientProfile.link({ patientId }));
    }, []);

    useEffect(() => {
      ScrollTableOnScreenToTop();
    }, [props.data]);

    const { sorting, setSorting } = useConvertedSorting(
      props.sorting,
      props.changeSorting,
      ScrollTableOnScreenToTop,
    );

    const tableColumns = useMemo(
      () => [
        {
          name: TableKeys.id,
          title: i18n.t(`columns.${TableKeys.id}` as any),
          getCellValue: (message: HL7MessageDto) => (
            <div className={styles.patientIdCell}>{message.id}</div>
          ),
        },
        {
          name: TableKeys.dateTime,
          title: i18n.t(`columns.${snakeCase(TableKeys.dateTime)}` as any),
          getCellValue: (message: HL7MessageDto) =>
            getFormattedDateTime(message.dateTime),
        },
        {
          name: TableKeys.type,
          title: i18n.t(`columns.${TableKeys.type}` as any),
          getCellValue: (message: HL7MessageDto) =>
            HL7MessageTypeNamesDict[message.type],
        },
        {
          name: TableKeys.messageDirection,
          title: i18n.t(
            `columns.${snakeCase(TableKeys.messageDirection)}` as any,
          ),
          getCellValue: (message: HL7MessageDto) =>
            message.messageDirection === HL7MessageDirection.Incoming
              ? i18n.t('incoming_message_status')
              : i18n.t('outgoing_message_status'),
        },
        {
          name: TableKeys.isAccepted,
          title: i18n.t(`columns.${snakeCase(TableKeys.isAccepted)}` as any),
          getCellValue: (message: HL7MessageDto) =>
            i18n.t(`status.${message.status}`),
        },
        {
          name: TableKeys.patient,
          title: i18n.t(`columns.${TableKeys.patient}` as any),
          getCellValue: (message: HL7MessageDto) => (
            <div
              onClick={() =>
                message.patient?.id && openPatientProfile(message.patient.id)
              }
              className={styles.patientNameInfo}
            >
              {getFullName(message.patient)}
            </div>
          ),
        },
        {
          name: TableKeys.document,
          title: i18n.t(`columns.${TableKeys.document}` as any),
          getCellValue: (message: HL7MessageDto) => (
            <div>
              <HL7DocumentItem
                document={props.documents.find(
                  (d) => d.id === message.outgoingFileId,
                )}
              />
            </div>
          ),
        },
        {
          name: TableKeys.error,
          title: i18n.t(`columns.${TableKeys.error}` as any),
          getCellValue: (message: HL7MessageDto) =>
            message.error && i18nGlobal.t(message.error as any),
        },
        {
          name: TableKeys.actions,
          title: '',
          getCellValue: (message: HL7MessageDto) => (
            <div onClick={(event: any) => event.stopPropagation()}>
              <TableDotMenu
                options={[
                  {
                    title: i18n.t('options.reparse_message'),
                    onClick: () => props.reparseMessage(message.id),
                    isHidden:
                      message.messageDirection !== HL7MessageDirection.Incoming,
                  },
                  {
                    title: i18n.t('options.reparse_message_with_filter'),
                    onClick: () => props.reparseMessageWithFilter(message.id),
                    isHidden:
                      message.messageDirection !== HL7MessageDirection.Incoming,
                  },
                  {
                    title: i18n.t('options.show_full_message'),
                    onClick: () => props.onShowFullMessage(message.id),
                  },
                  {
                    title: i18n.t('options.download'),
                    onClick: () => props.onDownloadMessage(message.id),
                  },
                  {
                    title: i18n.t('options.delete'),
                    onClick: () => props.onDelete(message.id),
                    isDeleteItem: true,
                    isUnderLine:
                      message.messageDirection == HL7MessageDirection.Incoming,
                  },
                ]}
              />
            </div>
          ),
        },
      ],
      [props.documents],
    );

    const tableColumnExtensions = useMemo<Table.ColumnExtension[]>(
      () => [
        { columnName: TableKeys.id },
        { columnName: TableKeys.dateTime, wordWrapEnabled: true },
        { columnName: TableKeys.type, wordWrapEnabled: true },
        { columnName: TableKeys.messageDirection },
        { columnName: TableKeys.isAccepted },
        { columnName: TableKeys.patient },
        { columnName: TableKeys.document },
        { columnName: TableKeys.error, wordWrapEnabled: true },
        { columnName: TableKeys.actions },
      ],
      [],
    );

    const defaultColumnWidths = useMemo<TableColumnWidthInfo[]>(
      () => [
        { columnName: TableKeys.id, width: '60px' },
        { columnName: TableKeys.dateTime, width: '10%' },
        { columnName: TableKeys.type, width: '15%' },
        { columnName: TableKeys.messageDirection, width: '8%' },
        { columnName: TableKeys.patient, width: '15%' },
        { columnName: TableKeys.document, width: '15%' },
        { columnName: TableKeys.isAccepted, width: '8%' },
        { columnName: TableKeys.error, width: 'auto' },
        { columnName: TableKeys.actions, width: '50px' },
      ],
      [],
    );

    const sortingColumnExtensions = useMemo<SortingState.ColumnExtension[]>(
      () => [
        { columnName: TableKeys.id, sortingEnabled: true },
        { columnName: TableKeys.dateTime, sortingEnabled: true },
        { columnName: TableKeys.type, sortingEnabled: true },
        { columnName: TableKeys.messageDirection, sortingEnabled: true },
      ],
      [],
    );

    //we need to lock neighbouring columns, otherwise user can change the width of the whole table, negating our fix
    const { columnWidths, onColumnWidthsChange } = useColumnResizingState(
      defaultColumnWidths,
      [TableKeys.actions, TableKeys.error],
    );

    const RowDetail = useCallback(
      (props: TableRowDetail.ContentProps) => (
        <div className={styles.content}>
          <Highlighter
            searchWords={searchText ? [searchText] : []}
            textToHighlight={props.row.content.replace(/(\r\n|\r|\n)/g, '\r\n')}
            autoEscape={true}
          />
        </div>
      ),
      [searchText],
    );

    if (props.noResults) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {i18n.t('empty_list_text')}
          </div>
        </div>
      );
    }

    return (
      <Paper style={{ height: '100%', width: '100%' }}>
        <DXRG.Grid
          rows={props.data ?? []}
          columns={tableColumns}
          getRowId={(message) => message.id}
          rootComponent={FullHeightGridRoot}
        >
          <DXRG.SortingState
            sorting={sorting}
            onSortingChange={setSorting}
            columnExtensions={sortingColumnExtensions}
            columnSortingEnabled={false}
          />
          <DXRG.RowDetailState />
          <DXRG.Table
            messages={localizationMessages}
            columnExtensions={tableColumnExtensions}
            tableComponent={HoverableTable}
          />
          <DXRG.TableColumnResizing
            columnWidths={columnWidths}
            onColumnWidthsChange={onColumnWidthsChange}
            resizingMode={'nextColumn'}
          />
          <DXRG.TableHeaderRow
            showSortingControls
            contentComponent={HideNullTitlesHeaderContent}
            rowComponent={StickyHeaderRow}
          />
          <DXRG.TableRowDetail contentComponent={RowDetail} />
        </DXRG.Grid>
      </Paper>
    );
  },
);
